import { Home, Server, ChevronRight, ArrowLeft, Layers, Wrench, ChevronLeft, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { confirmedRowRacks, rackLabel, type RackRowMembership } from '../../utils/rackName.logic';
import { isPowerDistributionEquipment } from '../../utils/rackPowerRedundancy.logic';
import {
  normalizeRackStatus,
  normalizeRackRole,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
} from '../../utils/rackLifecycle.logic';

interface RackConfig extends RackRowMembership {
  id: number;
  row_index: number;
  rack_index: number;
  name?: string;
  label?: string | null;
  u_slots?: number | null;
  max_weight_kg?: number;
  max_power_w?: number;
  type?: string;
  is_facility?: boolean;
}

interface RackItem extends RackRowMembership {
  id: number;
  row_index: number;
  rack_index: number;
  name?: string;
  label?: string | null;
  u_slots?: number | null;
}

interface RackEquipmentItem {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  weight_kg?: number | null;
  power_w?: number | null;
  is_zero_u?: boolean;
}

import type { SpaceMetrics } from '../../utils/rackReservation.logic';
import { calculateRackMetrics, formatEquipmentRating } from '../../utils/rackMetrics.logic';

interface CompactRackHeaderProps {
  datacenterCode?: string;
  datacenterName?: string;
  rack: RackConfig | null;
  racksList?: RackItem[];           // все стойки ЦОДа для дропдауна по ряду
  equipmentList: RackEquipmentItem[];
  spaceMetrics?: SpaceMetrics;
  projectionMode: 'front' | 'rear' | 'split';
  setProjectionMode: (mode: 'front' | 'rear' | 'split') => void;
  isMountingMode: boolean;
  isRefreshing?: boolean;
  setIsMountingMode: (val: boolean) => void;
  onBack: () => void;
  onEditRack?: () => void;
  onAddEquipment?: () => void;
  onProvision2NPdus?: () => void;
  theme?: 'dark' | 'light';
  prevRack?: { id: number; row_index: number; rack_index: number; label?: string | null; name?: string } | null;
  nextRack?: { id: number; row_index: number; rack_index: number; label?: string | null; name?: string } | null;
  onNavigateRack?: (rackId: number) => void;
}

export function CompactRackHeader({
  datacenterCode,
  datacenterName,
  rack,
  racksList = [],
  equipmentList,
  spaceMetrics,
  projectionMode,
  setProjectionMode,
  isMountingMode,
  isRefreshing = false,
  setIsMountingMode,
  onBack,
  onEditRack,
  onProvision2NPdus,
  theme: _theme = 'dark',
  prevRack,
  nextRack,
  onNavigateRack,
}: CompactRackHeaderProps) {
  const navigate = useNavigate();
  const racksInCurrentRow = confirmedRowRacks(racksList, rack);

  const isMotionOk = typeof window !== 'undefined'
    ? !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : true;

  if (!rack) return null;

  const totalU = rack.u_slots ?? 0;
  const metrics = spaceMetrics ?? calculateRackMetrics(equipmentList, [], totalU);


  return (
    <header
      aria-label="Навигация стойки"
      className="border-b px-3 py-1 flex flex-wrap items-center gap-2 font-mono text-xs z-30 shrink-0 select-none"
      style={{
        background: 'var(--bg-app)',
        borderColor: 'var(--border-color)',
        color: 'var(--text-primary)',
      }}
    >
      {/* Breadcrumb Navigation — min-w-0 allows it to shrink */}
      <div className="flex flex-wrap items-center gap-1.5 min-w-0 w-full lg:w-auto lg:flex-1" style={{ color: 'var(--text-muted)' }}>
        <button 
          onClick={onBack}
          className="min-h-11 min-w-11 flex items-center justify-center rounded-sm transition shrink-0 hover:text-[var(--accent)] hover:bg-[var(--bg-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
          style={{ color: 'var(--text-muted)' }}
          aria-label="Назад"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
        </button>

        {/* Home */}
        <button 
          onClick={() => navigate('/')}
          className="hidden sm:flex items-center justify-center min-h-11 min-w-11 transition shrink-0 hover:text-[var(--accent)]"
          style={{ color: 'var(--text-muted)' }}
          aria-label="Главная"
        >
          <Home className="w-3 h-3" aria-hidden="true" />
        </button>

        <ChevronRight className="hidden sm:block w-3 h-3 shrink-0" style={{ color: 'var(--text-muted)' }} />

        {/* Имя ЦОДа */}
        <button 
          onClick={() => navigate(`/${datacenterCode}/racks`)}
          className="min-h-11 max-w-[150px] truncate text-[11px] transition hover:text-[var(--accent)]"
          style={{ color: 'var(--text-muted)' }}
          title="Список стоек"
        >
          {datacenterName || datacenterCode?.toUpperCase()}
        </button>

        <ChevronRight className="w-3 h-3 shrink-0" style={{ color: 'var(--text-muted)' }} />

        {/* Confirmed row members; native select avoids clipped popovers. */}
        {racksInCurrentRow.length > 0 && (
          <select
            value={rack.id}
            onChange={e => onNavigateRack?.(Number(e.target.value))}
            aria-label="Выбрать стойку в этом ряду"
            title="Выбрать стойку в этом ряду"
            className="min-h-11 min-w-11 max-w-[200px] rounded-sm border px-2 text-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            style={{ color: 'var(--text-primary)', background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}
          >
            {racksInCurrentRow.map(r => (
              <option key={r.id} value={r.id}>Ряд {rack.row_index + 1} · {rackLabel(r)}</option>
            ))}
          </select>
        )}

        {racksInCurrentRow.length > 0 && (
          <ChevronRight className="w-3 h-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
        )}

        {/* Текущая стойка */}
        <div className="flex items-center gap-1 font-bold text-[11px] min-w-0" style={{ color: 'var(--text-primary)' }}>
          <Server className="w-3 h-3 shrink-0" style={{ color: 'var(--accent)' }} />
          <span className="truncate" title={rackLabel(rack)}>{rackLabel(rack)}</span>
        </div>

        {rack.address_confirmed === false && !rack.is_facility && rack.type !== 'pillar' && rack.type !== 'column' && (
          <span className="text-[11px]" title="Метка не является действующим адресом стойки.">
            Место не подтверждено
          </span>
        )}

        {/* Status & Role Badges */}
        {(() => {
          const status = normalizeRackStatus((rack as any)?.status);
          const role = normalizeRackRole((rack as any)?.role, rack?.type);
          const statusMeta = RACK_STATUS_METAS[status];
          const roleMeta = RACK_ROLE_METAS[role];
          return (
            <div className="flex items-center gap-1 shrink-0 ml-1">
              <span
                className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider shrink-0"
                style={{
                  background: statusMeta.badgeBg,
                  color: statusMeta.color,
                  border: `1px solid ${statusMeta.badgeBorder}`,
                }}
                title={`Статус: ${statusMeta.labelRu} (${statusMeta.description})`}
              >
                {statusMeta.labelRu}
              </span>
              <span
                className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider shrink-0 border"
                style={{
                  background: 'var(--bg-panel)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                }}
                title={`Роль: ${roleMeta.labelRu} (${roleMeta.description})`}
              >
                {roleMeta.shortCode}
              </span>
              {(rack as any)?.tenant && (rack as any)?.tenant !== 'mine' && (
                <span
                  data-testid="header-rack-tenant-badge"
                  className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold font-mono tracking-wider shrink-0 border"
                  style={{
                    background: 'rgba(168, 85, 247, 0.15)',
                    color: '#c084fc',
                    borderColor: 'rgba(168, 85, 247, 0.4)',
                  }}
                  title={`Владелец стойки: ${(rack as any)?.tenant}`}
                >
                  {(rack as any)?.tenant}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Center Micro Telemetry Badges — hidden below 1536px */}
      <div className="hidden 2xl:flex flex-wrap min-w-0 max-w-full items-center gap-3 text-[10px]">
        <div
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm border"
          style={{
            background: 'var(--bg-panel)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>ЮНИТЫ:</span>
          {rack.u_slots == null ? <span>Вместимость неизвестна</span> : <>
          <span className="font-bold tabular-nums" style={{ color: 'var(--accent)' }}>
            {metrics.occupiedU}U ЗАН
          </span>
          {spaceMetrics && spaceMetrics.reservedU > 0 && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <span className="font-bold tabular-nums" style={{ color: '#f59e0b' }}>
                {spaceMetrics.reservedU}U РЕЗ
              </span>
            </>
          )}
          <span style={{ color: 'var(--text-muted)' }}>·</span>
          <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>
            {metrics.availableU}U СВОБ
          </span>
          <span className="font-bold tabular-nums" style={{ color: 'var(--text-muted)' }}>
            ({metrics.utilizationPct}%)
          </span>
          </>}
        </div>

        <div
          className="flex flex-wrap min-w-0 max-w-full items-center gap-1 px-2 py-0.5 rounded-sm border"
          style={{
            background: 'var(--bg-panel)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>МОЩНОСТЬ:</span>
          <span className="font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{formatEquipmentRating(equipmentList, 'power_w', 'Вт')}</span>
        </div>

        <div
          className="flex flex-wrap min-w-0 max-w-full items-center gap-1 px-2 py-0.5 rounded-sm border"
          style={{
            background: 'var(--bg-panel)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>МАССА:</span>
          <span className="font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{formatEquipmentRating(equipmentList, 'weight_kg', 'кг')}</span>
        </div>
      </div>

      {/* Projection View Controls & Mounting Toggle — shrink-0 so they never clip */}
      <div className="flex flex-wrap items-center gap-1.5 max-w-full">
        
        {/* Projection Selector */}
        <div
          className="flex p-0.5 rounded-sm border text-[10px]"
          style={{
            background: 'var(--bg-input)',
            borderColor: 'var(--border-color)',
          }}
        >
          <button
            onClick={() => setProjectionMode('split')}
            className="px-1.5 py-0.5 rounded-sm transition whitespace-nowrap"
            style={{
              background: projectionMode === 'split' ? 'var(--accent)' : 'transparent',
              color: projectionMode === 'split' ? 'var(--text-on-accent, #ffffff)' : 'var(--text-muted)',
              fontWeight: projectionMode === 'split' ? 700 : 500,
            }}
            title="Фронт и тыл одновременно"
            aria-pressed={projectionMode === 'split'}
          >
            Split
          </button>
          <button
            onClick={() => setProjectionMode('front')}
            className="px-1.5 py-0.5 rounded-sm transition whitespace-nowrap"
            style={{
              background: projectionMode === 'front' ? 'var(--accent)' : 'transparent',
              color: projectionMode === 'front' ? 'var(--text-on-accent, #ffffff)' : 'var(--text-muted)',
              fontWeight: projectionMode === 'front' ? 700 : 500,
            }}
            title="Фронтальная панель"
          >
            Фронт
          </button>
          <button
            onClick={() => setProjectionMode('rear')}
            className="px-1.5 py-0.5 rounded-sm transition whitespace-nowrap"
            style={{
              background: projectionMode === 'rear' ? 'var(--accent)' : 'transparent',
              color: projectionMode === 'rear' ? 'var(--text-on-accent, #ffffff)' : 'var(--text-muted)',
              fontWeight: projectionMode === 'rear' ? 700 : 500,
            }}
            title="Задняя панель"
          >
            Тыл
          </button>
        </div>

        {/* Mounting Mode Toggle */}
        <button
          onClick={() => setIsMountingMode(!isMountingMode)}
          disabled={isRefreshing || rack.u_slots == null || totalU <= 0}
          aria-pressed={isMountingMode}
          aria-label={isMountingMode ? 'Режим монтажа активен' : 'Режим монтажа выключен'}
          className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold border transition flex items-center gap-1 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
            isMountingMode && isMotionOk ? 'animate-pulse' : ''
          }`}
          style={{
            background: isMountingMode ? 'rgba(245, 158, 11, 0.15)' : 'var(--bg-input)',
            borderColor: isMountingMode ? 'rgba(245, 158, 11, 0.6)' : 'var(--border-color)',
            color: isMountingMode ? '#f59e0b' : 'var(--text-muted)',
          }}
          title="Режим перетаскивания и установки приборов мышью"
        >
          <Layers className="w-3 h-3" aria-hidden="true" />
          {isMountingMode ? 'Вкл' : 'Монтаж'}
        </button>

        {equipmentList.filter(isPowerDistributionEquipment).length < 2 && onProvision2NPdus && (
          <button
            onClick={onProvision2NPdus}
            className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold border transition flex items-center gap-1 whitespace-nowrap cursor-pointer hover:opacity-90"
            style={{
              background: 'rgba(6, 182, 212, 0.12)',
              borderColor: 'rgba(6, 182, 212, 0.4)',
              color: 'var(--accent, #06b6d4)',
            }}
            title="Добавить PDU A/B во вкладке Питание"
          >
            <Zap className="w-3 h-3" aria-hidden="true" />
            <span>+ PDU A/B</span>
          </button>
        )}

        {onEditRack && (
          <button
            onClick={onEditRack}
            className="p-1 rounded-sm transition hover:text-[var(--accent)] hover:bg-[var(--bg-panel)]"
            style={{ color: 'var(--text-muted)' }}
            title="Параметры стойки"
          >
            <Wrench className="w-3.5 h-3.5" />
          </button>
        )}

        {/* ── Rack Row Navigation ── */}
        {racksInCurrentRow.length > 0 && (prevRack || nextRack) && (
          <div
            className="flex items-center border rounded-sm text-[10px] font-mono overflow-hidden"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <button
              disabled={!prevRack}
              onClick={() => prevRack && onNavigateRack?.(prevRack.id)}
              className="min-h-11 min-w-11 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition hover:text-[var(--accent)] hover:bg-[var(--bg-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-[-2px]"
              style={{ color: 'var(--text-muted)' }}
              title={prevRack ? `← ${rackLabel(prevRack)}` : 'Первая стойка в ряду'}
              aria-label="Предыдущая стойка в ряду"
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span
              className="px-1.5 text-[9px] border-x"
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}
            >
              {rack.rack_pos_in_row != null ? `С${rack.rack_pos_in_row}` : '—'}
            </span>
            <button
              disabled={!nextRack}
              onClick={() => nextRack && onNavigateRack?.(nextRack.id)}
              className="min-h-11 min-w-11 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition hover:text-[var(--accent)] hover:bg-[var(--bg-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-[-2px]"
              style={{ color: 'var(--text-muted)' }}
              title={nextRack ? `${rackLabel(nextRack)} →` : 'Последняя стойка в ряду'}
              aria-label="Следующая стойка в ряду"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Theme toggle removed — now in global header only */}
      </div>

    </header>
  );
}
